import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { API, WHATSAPP } from "@/lib/api";

export const AssistantWidget = () => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "bot", text: "Hola, soy el asistente virtual de Nilo. Puedo contarte sobre los servicios, la modalidad de trabajo y cómo solicitar un turno. La información es orientativa y no reemplaza una evaluación profesional." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), [msgs, open]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const q = input.trim();
    setInput("");
    setMsgs((m) => [...m, { role: "user", text: q }, { role: "bot", text: "" }]);
    setLoading(true);

    const push = (delta) =>
      setMsgs((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "bot", text: copy[copy.length - 1].text + delta };
        return copy;
      });

    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 60000);
    try {
      const res = await fetch(`${API}/assistant/stream?mensaje=${encodeURIComponent(q)}`, { signal: ctrl.signal });
      if (!res.ok || !res.body) throw new Error("stream");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line || line === "[DONE]") continue;
          try { push(JSON.parse(line).delta || ""); } catch { /* ignore */ }
        }
      }
    } catch {
      push("No pude conectarme en este momento. Escribinos por WhatsApp al 02262-484516.");
    } finally {
      clearTimeout(timer);
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="assistant-toggle"
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full btn-gold shadow-lg"
      >
        {open ? <X size={20} /> : <MessageCircle size={20} />}
      </button>
      {open && (
        <div data-testid="assistant-panel" className="fixed bottom-24 right-4 z-50 flex h-[26rem] w-[calc(100vw-2rem)] max-w-[22rem] flex-col rounded-sm border border-[#dcd5ce] bg-white shadow-2xl">
          <div className="border-b border-[#ebe5df] px-4 py-3">
            <p className="serif text-lg">Asistente Nilo</p>
            <p className="text-xs text-[#8b8480]">Información orientativa</p>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto p-4 text-sm">
            {msgs.map((m, i) => (
              <div key={i} className={m.role === "user" ? "text-right" : ""}>
                {m.text ? (
                  <span className={`inline-block max-w-[85%] whitespace-pre-wrap rounded-sm px-3 py-2 text-left ${m.role === "user" ? "bg-[#c5a059] text-white" : "bg-[#f4f1ec] text-[#2a2826]"}`}>
                    {m.text}
                  </span>
                ) : (
                  <span className="text-xs text-[#8b8480]">Escribiendo...</span>
                )}
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="flex gap-2 border-t border-[#ebe5df] p-3">
            <input
              data-testid="assistant-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Escribí tu consulta"
              className="flex-1 border border-[#dcd5ce] px-3 py-2 text-sm outline-none focus:border-[#c5a059]"
            />
            <button onClick={send} disabled={loading} data-testid="assistant-send" className="btn-gold px-3">
              <Send size={16} />
            </button>
          </div>
          <a href={WHATSAPP} target="_blank" rel="noreferrer" className="border-t border-[#ebe5df] py-2 text-center text-xs text-[#8b7355]">
            Prefiero escribir por WhatsApp
          </a>
        </div>
      )}
    </>
  );
};
